import React, { useMemo } from 'react'
import { Select, type SelectOption } from './Select'
import { useTimeline } from '../../context/useTimeline'
import { useNls } from '../../nls/nlsProvider'

export interface LinkedGoalPickerProps {
  value: string | null
  onChange: (value: string | null) => void
  disabled?: boolean
  className?: string
  ariaLabel?: string
}

export const LinkedGoalPicker: React.FC<LinkedGoalPickerProps> = ({
  value,
  onChange,
  disabled = false,
  className,
  ariaLabel,
}) => {
  const { t } = useNls()
  const { elements, pathOnlyElementIds } = useTimeline()

  const readableGoals = useMemo(() => {
    if (!elements) return []
    return elements.filter((el) => el.type === 'GOAL' && !pathOnlyElementIds?.has(el.id))
  }, [elements, pathOnlyElementIds])

  const options: SelectOption<string>[] = useMemo(() => [
    { value: '', label: t('NLSLinkedGoalNone') },
    ...readableGoals.map((g) => ({
      value: g.id,
      label: g.title || g.id,
    })),
  ], [readableGoals, t])

  const handleSelectChange = (nextValue: string) => {
    onChange(nextValue.trim() ? nextValue.trim() : null)
  }

  const labelText = t('NLSLinkedGoalLabel')
  const hintText = t('NLSLinkedGoalHint')

  return (
    <div className={`linkedGoalPicker ${className ?? ''}`.trim()} data-testid="linked-goal-picker">
      <label className="linkedGoalPicker__label">
        <span className="linkedGoalPicker__labelText">{labelText}</span>
        <Select<string>
          trigger="pill"
          ariaLabel={ariaLabel ?? labelText}
          value={value ?? ''}
          options={options}
          disabled={disabled}
          onChange={handleSelectChange}
        />
      </label>
      <div className="linkedGoalPicker__hint">{hintText}</div>
    </div>
  )
}
